import { type Dispatch, type SetStateAction, memo } from "react";
import SectionTitle from "../../../SectionTitle";
import usePurchases from "../../../../hooks/usePurchases";
import { ButtonAccessability } from "../../../../../../Elements/Button/Button";
import { useContent } from "../../../../../../../contexts/contentContext/contentContext";
import { PaymentTypeE, PurchaseStepE } from "../../../../models/enums";
import useCreditCard from "../../../../hooks/useCreditCard";
import CreditCardDetails from "../CreditCardDetails";
import Image from "../../../../../../Elements/Image/Image";

import styles from "./styles.module.scss";

interface SelectedPaymentMethodSectionI {
  setShowPaymentTypeList: Dispatch<SetStateAction<boolean>>;
}

/**
 * Selected payment method section displays when payment method is selected
 * and user went to the next step.
 *
 * This section allows to go back by clicking change-payment-method button
 * and choose another payment method and other settings
 * like coupon code or period.
 */
const SelectedPaymentMethodSection = memo(
  (props: SelectedPaymentMethodSectionI) => {
    const { setShowPaymentTypeList } = props;
    const { getTextByKey } = useContent();
    const { setCompletedStep } = usePurchases();
    const { paymentType, selectedCreditCardId } = useCreditCard();

    // Go back and show payment type list if exists
    const handleChoosePaymentMethod = () => {
      setCompletedStep(PurchaseStepE.PRIMARY_PURCHASE_STEP);
      setShowPaymentTypeList(true);
    };

    return (
      <div className={styles.selectedPaymentMethodSectionWrapper}>
        <SectionTitle titleTextKey="purchase-payment-method-title" step={1} />
        {paymentType === PaymentTypeE.PAYPAL ? (
          <Image name="purchases/paypal-button" ext="png" />
        ) : null}
        {selectedCreditCardId && paymentType === PaymentTypeE.CREDIT_CARD ? (
          <CreditCardDetails
            creditCardId={selectedCreditCardId}
            paymentType={paymentType!}
          />
        ) : null}
        {(!selectedCreditCardId && paymentType === PaymentTypeE.CREDIT_CARD) ||
        paymentType === PaymentTypeE.NEW_CREDIT_CARD ? (
          <div className={styles.creditCardLine}>
            <div className={styles.newCreditCard}>
              {getTextByKey("new-credit-card")}

              <Image
                name="purchases/credit-card_icon"
                alt="credit card"
                title={getTextByKey("credit-card")}
              />
            </div>
          </div>
        ) : null}
        <ButtonAccessability
          className={styles.changePaymentMethod}
          onClick={handleChoosePaymentMethod}
        >
          {getTextByKey("change-payment-method")}
        </ButtonAccessability>
      </div>
    );
  }
);

export default SelectedPaymentMethodSection;
